/**
 * What a setting is, and how its value survives a round trip.
 *
 * The vocabulary the settings panel is built from, with nothing in it about
 * which settings there are. One tool has a panel — the pinyin converter, whose
 * settings are in `controls.ts` — and this file is what keeps the markup, the
 * storage and the address bar from having to know that. The script converter
 * had a panel too until it started writing every script at once and had
 * nothing left to ask.
 *
 * Everything a value can arrive from is untrusted. A radio's `value` attribute
 * is a string even where the setting is a boolean, a URL was typed by hand, and
 * `localStorage` may have been written by a version of the tool that offered
 * choices this one does not. All three are answered the same way, by asking the
 * control what it actually offers.
 */

/** One choice within a control, as it appears on its button. */
export interface ControlValue {
  readonly value: string | boolean;
  readonly label: string;
}

/** One setting, as a labelled row of buttons. */
export interface Control<Field extends string = string> {
  readonly field: Field;
  readonly label: string;
  /** A sentence saying what the setting decides, shown under the row. */
  readonly note: string;
  readonly values: readonly ControlValue[];
}

/** How a value is written into a radio's `value` attribute. */
export function valueAttribute(value: string | boolean): string {
  return typeof value === "boolean" ? String(value) : value;
}

/**
 * What a set of settings currently says about one field.
 *
 * The panel is drawn from a list of controls and a set of settings that the
 * list is about, and the two are only ever connected by the field name. This
 * is the join, and it is a function rather than an index because the settings
 * are written as interfaces with named fields: those describe the tools better
 * than a bag of strings would, and the cost of that is one narrowing here
 * instead of an index signature on every one of them.
 *
 * A field the settings do not hold, or hold something odd under, reads as
 * undefined. That is the same answer an unrecognised value gets everywhere
 * else in this file, and for the same reason.
 */
export function settingValue(
  settings: object,
  field: string,
): string | boolean | undefined {
  const value = (settings as Record<string, unknown>)[field];

  return typeof value === "string" || typeof value === "boolean"
    ? value
    : undefined;
}

/**
 * Read a control's chosen value back, given what the radio carried.
 *
 * The attribute is always a string, so the booleans have to be recovered from
 * what the control offers rather than guessed at from the text.
 */
export function valueFromAttribute(
  control: Control,
  attribute: string,
): string | boolean | undefined {
  return control.values.find(
    (offered) => valueAttribute(offered.value) === attribute,
  )?.value;
}

/**
 * Read a set of settings off something untrusted.
 *
 * Every field is checked against the values its control offers and anything
 * that fails falls back to the default, because the input is whatever was left
 * in `localStorage`, possibly by an older version of this tool.
 */
export function readSettings<Settings extends object>(
  controls: readonly Control[],
  defaults: Settings,
  value: unknown,
): Settings {
  if (typeof value !== "object" || value === null) {
    return defaults;
  }

  let read = defaults;

  for (const control of controls) {
    const offered: readonly (string | boolean)[] = control.values.map(
      (each) => each.value,
    );
    const stored = settingValue(value, control.field);

    if (stored !== undefined && offered.includes(stored)) {
      read = { ...read, [control.field]: stored };
    }
  }

  return read;
}
